import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { isGameMaster } from '../lib/utils';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  History,
  Plus,
  Trash2,
  Calendar,
  Swords,
  BookOpen,
  Eye,
  Scroll,
  Loader2,
} from 'lucide-react';

const EVENT_ICONS = {
  historical: History,
  session: Calendar,
  discovery: BookOpen,
  battle: Swords,
};

const EVENT_COLORS = {
  historical: 'border-l-blue-500 bg-blue-500/5',
  session: 'border-l-green-500 bg-green-500/5',
  discovery: 'border-l-purple-500 bg-purple-500/5',
  battle: 'border-l-red-500 bg-red-500/5',
};

export default function TimelinePage() {
  const { campaignId } = useParams();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  const [form, setForm] = useState({
    title: '',
    description: '',
    date_in_world: '',
    era: '',
    event_type: 'historical',
    visibility: 'players',
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`/api/campaigns/${campaignId}/timeline`);
        setEvents(res.data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    if (campaignId) fetchEvents();
  }, [campaignId]);

  const handleCreate = async () => {
    if (!form.title || !form.date_in_world) return;
    setCreating(true);
    try {
      const res = await axios.post(`/api/campaigns/${campaignId}/timeline`, form);
      setEvents(prev => [...prev, res.data].sort((a, b) => a.date_in_world.localeCompare(b.date_in_world)));
      setCreateOpen(false);
      setForm({ title: '', description: '', date_in_world: '', era: '', event_type: 'historical', visibility: 'players' });
      toast.success(language === 'fr' ? 'Événement créé' : 'Event created');
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`/api/timeline/${eventId}`);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast.success(language === 'fr' ? 'Événement supprimé' : 'Event deleted');
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const filteredEvents = filterType === 'all'
    ? events
    : events.filter(e => e.event_type === filterType);

  // Group by era
  const grouped = {};
  for (const event of filteredEvents) {
    const era = event.era || (language === 'fr' ? 'Sans période' : 'No era');
    if (!grouped[era]) grouped[era] = [];
    grouped[era].push(event);
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="timeline-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-cinzel text-2xl font-bold flex items-center gap-2">
              <History className="h-6 w-6 text-primary" />
              {language === 'fr' ? 'Chronologie' : 'Timeline'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr'
                ? `${events.length} événement${events.length > 1 ? 's' : ''}`
                : `${events.length} event${events.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                <SelectItem value="historical">{language === 'fr' ? 'Historique' : 'Historical'}</SelectItem>
                <SelectItem value="session">Session</SelectItem>
                <SelectItem value="discovery">{language === 'fr' ? 'Découverte' : 'Discovery'}</SelectItem>
                <SelectItem value="battle">{language === 'fr' ? 'Bataille' : 'Battle'}</SelectItem>
              </SelectContent>
            </Select>

            {isMJ && (
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Ajouter' : 'Add'}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-cinzel">
                      {language === 'fr' ? 'Nouvel événement' : 'New Event'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label>{language === 'fr' ? 'Titre' : 'Title'} *</Label>
                      <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Date (dans le monde)' : 'In-world date'} *</Label>
                      <Input value={form.date_in_world} onChange={e => setForm(f => ({ ...f, date_in_world: e.target.value }))} placeholder="An 1432, Mois de Kythorn" />
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Période / Ère' : 'Era'}</Label>
                      <Input value={form.era} onChange={e => setForm(f => ({ ...f, era: e.target.value }))} placeholder="Âge des Dragons" />
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Type' : 'Type'}</Label>
                      <Select value={form.event_type} onValueChange={v => setForm(f => ({ ...f, event_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="historical">{language === 'fr' ? 'Historique' : 'Historical'}</SelectItem>
                          <SelectItem value="session">Session</SelectItem>
                          <SelectItem value="discovery">{language === 'fr' ? 'Découverte' : 'Discovery'}</SelectItem>
                          <SelectItem value="battle">{language === 'fr' ? 'Bataille' : 'Battle'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('cancel', language)}</Button>
                    <Button onClick={handleCreate} disabled={creating || !form.title || !form.date_in_world}>
                      {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('create', language)}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{language === 'fr' ? 'Aucun événement dans la chronologie' : 'No timeline events yet'}</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border" />

            <div className="space-y-1">
              {Object.entries(grouped).map(([era, eraEvents]) => (
                <div key={era} className="space-y-3">
                  {/* Era header */}
                  <div className="relative flex items-center gap-3 pl-12 py-2">
                    <div className="absolute left-4 w-4 h-4 rounded-full bg-primary border-2 border-background" />
                    <Badge variant="secondary" className="font-cinzel text-xs">{era}</Badge>
                  </div>

                  {eraEvents.map((event) => {
                    const Icon = EVENT_ICONS[event.event_type] || History;
                    const colorClass = EVENT_COLORS[event.event_type] || 'border-l-border';
                    return (
                      <div key={event.id} className="relative pl-12">
                        <div className="absolute left-[19px] w-2.5 h-2.5 rounded-full bg-muted-foreground/30 border-2 border-background mt-4" />
                        <Card className={`border-l-4 ${colorClass}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-3">
                                <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                                <div>
                                  <h3 className="font-medium text-sm">{event.title}</h3>
                                  <p className="text-xs text-muted-foreground mt-0.5">{event.date_in_world}</p>
                                  {event.description && (
                                    <p className="text-sm text-muted-foreground mt-2">{event.description}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Badge variant="outline" className="text-[10px]">{event.event_type}</Badge>
                                {isMJ && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(event.id)}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
